// src/context/ThemeContext.jsx
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { settingsApi } from '../api/settingsApi';

const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemeContext must be used within a ThemeProvider');
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [colors, setColors] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const res = await settingsApi.getThemeSettings();
        
        // ✅ FIX: settingsApi already returns response.data
        // If res is the theme data directly, use it
        // If res has a .data property, use that
        const themeData = res.data || res || {};
        
        setColors({
          primaryColor: themeData.primaryColor || '#d32f2f',
          secondaryColor: themeData.secondaryColor || '#1976d2',
          backgroundColor: themeData.backgroundColor || '#f5f5f5',
          paperColor: themeData.paperColor || '#ffffff',
          textPrimary: themeData.textPrimary || '#212121',
          textSecondary: themeData.textSecondary || '#757575',
          errorColor: themeData.errorColor || '#f44336',
          warningColor: themeData.warningColor || '#ff9800',
          infoColor: themeData.infoColor || '#2196f3',
          successColor: themeData.successColor || '#4caf50',
          themeMode: themeData.themeMode || 'light',
        });
      } catch (err) {
        // Fallback defaults if DB fails
        setColors({
          primaryColor: '#d32f2f',
          secondaryColor: '#1976d2',
          backgroundColor: '#f5f5f5',
          paperColor: '#ffffff',
          textPrimary: '#212121',
          textSecondary: '#757575',
          errorColor: '#f44336',
          warningColor: '#ff9800',
          infoColor: '#2196f3',
          successColor: '#4caf50',
          themeMode: 'light'
        });
      } finally {
        setLoading(false);
      }
    };
    fetchTheme();
  }, []);

  // Update CSS Variables for Scrollbar and others whenever colors change
  useEffect(() => {
    if (colors) {
      const root = document.documentElement;
      
      root.style.setProperty('--scrollbar-color', colors.primaryColor);
      root.style.setProperty('--scrollbar-color-hover', colors.primaryColor);
      
      if (colors.themeMode === 'dark') {
        document.body.classList.add('dark-mode');
        document.body.classList.remove('light-mode');
      } else {
        document.body.classList.add('light-mode');
        document.body.classList.remove('dark-mode');
      }
    }
  }, [colors]);

  const updateColors = async (newData) => {
    try {
      await settingsApi.updateThemeSettings(newData);
      setColors((prev) => {
        const updated = { ...prev, ...newData };
        localStorage.setItem('themeColors', JSON.stringify(updated));
        return updated;
      });
    } catch (error) {
      console.error('Failed to save theme:', error);
      setColors((prev) => {
        const updated = { ...prev, ...newData };
        localStorage.setItem('themeColors', JSON.stringify(updated));
        return updated;
      });
    }
  };

  const toggleTheme = () => {
    if (!colors) return;
    const newMode = colors.themeMode === 'dark' ? 'light' : 'dark';
    updateColors({ themeMode: newMode });
  };

  const getThemeMode = () => {
    return colors?.themeMode || 'light';
  };

  const isDarkMode = () => {
    return colors?.themeMode === 'dark';
  };

  const muiTheme = useMemo(() => {
    if (!colors) return createTheme();
    const isDark = colors.themeMode === 'dark';
    return createTheme({
      palette: {
        mode: colors.themeMode,
        primary: { main: colors.primaryColor },
        secondary: { main: colors.secondaryColor },
        error: { main: colors.errorColor },
        warning: { main: colors.warningColor },
        info: { main: colors.infoColor },
        success: { main: colors.successColor },
        background: {
          default: isDark ? '#121212' : colors.backgroundColor,
          paper: isDark ? '#1e1e1e' : colors.paperColor,
        },
        text: {
          primary: isDark ? '#ffffff' : colors.textPrimary,
          secondary: isDark ? '#aaaaaa' : colors.textSecondary,
        },
      },
      components: {
        MuiAppBar: {
          styleOverrides: {
            root: {
              backgroundColor: isDark ? '#1e1e1e' : colors.paperColor,
              color: isDark ? '#ffffff' : colors.textPrimary,
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: isDark ? '#1e1e1e' : colors.paperColor,
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundColor: isDark ? '#1e1e1e' : colors.paperColor,
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              backgroundColor: isDark ? '#1e1e1e' : colors.paperColor,
            },
          },
        },
      },
    });
  }, [colors]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <div>Loading theme...</div>
      </div>
    );
  }

  const contextValue = {
    colors,
    updateColors,
    toggleTheme,
    getThemeMode,
    isDarkMode,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};